// ============================================================================
// Invoice detail (/invoicing/:invoiceId) — header, totals, line items, and the
// status spine. Status transitions reuse the existing invoice RPCs
// (validate_invoice_hours → approve_invoice → mark_invoice_sent →
// mark_invoice_paid; void_invoice). Phase 1.5: mark-sent is status-only (no
// QBO write). Action buttons are gated to finance/exec/superuser (+ hr for
// hours validation), mirroring invoices RLS.
// ============================================================================

import { canValidateInvoiceHours, hasFinanceCapability } from '../../shared/role-gates';
import { Fragment, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SmartSelect } from '../../components/smart-select';
import { projectRowName, type ProjectRow } from '../project-work/lib/project-rows';
import { ArrowLeft, Ban, CheckCircle2, DollarSign, Send } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import { Button, ErrorState, PageHeader, Skeleton, useToast } from '../../shared/components';
import { fmtDate } from '../../shared/format';

import {
  approveInvoice,
  changeInvoiceLineProject,
  markInvoicePaid,
  markInvoiceSent,
  useInvoice,
  validateInvoiceHours,
  voidInvoice,
} from './hooks/use-invoicing-data';
import { fmtHours, fmtMoney } from './types';
import type { InvoiceLineItemRow } from './types';
import { InvoiceStatusBadge } from './invoicing-queue';
import { errorMessage } from '../../shared/errors';
import { canChangeProjectOn, isRepointTarget } from './repoint';

export default function InvoiceDetail() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { profile } = useAuth();
  // Finance-capable = primary finance/exec/superuser OR extra_roles 'finance'
  // hat (Antonela). Mirrors the invoices RLS, now on has_finance_capability().
  const canManage = hasFinanceCapability(profile);
  const canValidate = canValidateInvoiceHours(profile);

  const toast = useToast();
  const { data: invoice, lineItems, projects, isLoading, error, refetch } = useInvoice(invoiceId ?? null);
  const [busy, setBusy] = useState<string | null>(null);

  async function run(action: string, fn: () => Promise<void>, successMsg: string) {
    setBusy(action);
    try {
      await fn();
      toast.success(successMsg);
      refetch();
    } catch (err) {
      toast.error(errorMessage(err, 'Action failed'));
    } finally {
      setBusy(null);
    }
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-3 p-4 md:p-6">
        <Skeleton block height={32} width="40%" />
        <Skeleton block height={120} width="100%" />
        <Skeleton block height={200} width="100%" />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="mx-auto max-w-3xl p-4 md:p-6">
        <BackLink />
        <ErrorState
          title="Couldn't load the invoice"
          body={error ?? 'Invoice not found, or you do not have access to it.'}
          onRetry={refetch}
        />
      </div>
    );
  }

  const status = invoice.status;
  const isHourly = invoice.billing_model === 'Hourly' || invoice.billing_model === 'Hybrid';

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 md:p-6">
      <BackLink />
      <PageHeader
        eyebrow={invoice.facilities?.facility_name ?? invoice.accounts?.account_name ?? invoice.account_id}
        title={
          <span className="flex items-center gap-2.5">
            <span className="font-mono text-xl">{invoice.invoice_number ?? invoice.invoice_id}</span>
            <InvoiceStatusBadge status={status} />
          </span>
        }
        meta={`${fmtDate(invoice.billing_period_start)} – ${fmtDate(invoice.billing_period_end)} · ${invoice.billing_model}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canValidate && status === 'Pending_Hours_Validation' && (
              <Button
                size="sm"
                icon={<CheckCircle2 className="h-4 w-4" />}
                loading={busy === 'validate'}
                onClick={() =>
                  run('validate', () => validateInvoiceHours(invoice.invoice_id, null), 'Hours validated')
                }
              >
                Validate hours
              </Button>
            )}
            {canManage && ['Draft', 'Pending_Finance_Review'].includes(status) && (
              <Button
                size="sm"
                icon={<CheckCircle2 className="h-4 w-4" />}
                loading={busy === 'approve'}
                onClick={() => run('approve', () => approveInvoice(invoice.invoice_id, null), 'Invoice approved')}
              >
                Approve
              </Button>
            )}
            {canManage && status === 'Approved' && (
              <Button
                size="sm"
                icon={<Send className="h-4 w-4" />}
                loading={busy === 'sent'}
                onClick={() => run('sent', () => markInvoiceSent(invoice.invoice_id, null), 'Marked sent')}
              >
                Mark sent
              </Button>
            )}
            {canManage && status === 'Sent' && (
              <Button
                size="sm"
                icon={<DollarSign className="h-4 w-4" />}
                loading={busy === 'paid'}
                onClick={() => {
                  const def = invoice.total_amount != null ? String(invoice.total_amount) : '';
                  const raw = window.prompt('Paid amount (USD):', def);
                  if (raw == null) return;
                  const amt = Number(raw);
                  if (!Number.isFinite(amt) || amt < 0) {
                    toast.error('Enter a valid amount');
                    return;
                  }
                  run('paid', () => markInvoicePaid(invoice.invoice_id, amt), 'Marked paid');
                }}
              >
                Mark paid
              </Button>
            )}
            {canManage && !['Paid', 'Voided'].includes(status) && (
              <Button
                size="sm"
                variant="destructive"
                icon={<Ban className="h-4 w-4" />}
                loading={busy === 'void'}
                onClick={() => {
                  const reason = window.prompt('Reason for voiding this invoice:');
                  if (!reason) return;
                  run('void', () => voidInvoice(invoice.invoice_id, reason), 'Invoice voided');
                }}
              >
                Void
              </Button>
            )}
          </div>
        }
      />

      {/* Totals */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <TotalCard label="Total" value={fmtMoney(invoice.total_amount)} strong />
        {isHourly && <TotalCard label="Hours" value={fmtHours(invoice.total_hours)} />}
        {isHourly && <TotalCard label="Hours subtotal" value={fmtMoney(invoice.hours_subtotal)} />}
        {invoice.monthly_rate_amount != null && (
          <TotalCard label="Monthly" value={fmtMoney(invoice.monthly_rate_amount)} />
        )}
        {invoice.project_subtotal != null && invoice.project_subtotal > 0 && (
          <TotalCard label="Projects" value={fmtMoney(invoice.project_subtotal)} />
        )}
        {invoice.paid_amount != null && <TotalCard label="Paid" value={fmtMoney(invoice.paid_amount)} />}
      </div>

      {/* Line items */}
      <section className="overflow-hidden rounded-lg border border-tcs-border bg-tcs-bg">
        <div className="border-b border-tcs-border px-4 py-2.5">
          <h2 className="text-sm font-semibold text-tcs-text">Line items ({lineItems.length})</h2>
        </div>
        {lineItems.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-tcs-text-muted">No line items.</div>
        ) : (
          <LineItemsTable
            items={lineItems}
            projects={projects}
            canChangeProject={canChangeProjectOn(status, canManage)}
            facilityId={invoice.facility_id}
            onChanged={refetch}
          />
        )}
      </section>

      {invoice.notes && (
        <div className="rounded-lg border border-tcs-border bg-tcs-bg p-4 text-sm text-tcs-text-secondary">
          <span className="font-medium text-tcs-text">Notes: </span>
          {invoice.notes}
        </div>
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/invoicing"
      className="inline-flex items-center gap-1 text-sm text-tcs-text-secondary hover:text-tcs-text"
    >
      <ArrowLeft className="h-4 w-4" /> Invoicing
    </Link>
  );
}

function TotalCard({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-bg p-3">
      <div className="text-[10px] font-medium uppercase tracking-wide text-tcs-text-muted">{label}</div>
      <div className={`mt-1 font-mono ${strong ? 'text-lg font-semibold text-tcs-text' : 'text-sm text-tcs-text-secondary'}`}>
        {value}
      </div>
    </div>
  );
}

/**
 * The line items. Item #542 part 2 (TCSD-0490; Ben's go on the demo, 2026-10-19):
 *   - a Project column, shown when any line bills a project, naming it and
 *     linking to it;
 *   - "Change project" on each project line, for Finance only, and only before
 *     the bill is sent (Decision 1). It opens SmartSelect — THE picker
 *     (TCSD-0171) — scoped to this building. Only a project that is Ready to
 *     Invoice and on no invoice can be picked (Decision 2); the rest are listed
 *     greyed with the reason. The move itself is change_invoice_line_project.
 */
export function LineItemsTable({
  items,
  projects,
  canChangeProject,
  facilityId,
  onChanged,
}: {
  items: InvoiceLineItemRow[];
  projects: Record<string, ProjectRow>;
  canChangeProject: boolean;
  facilityId: string | null;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [changing, setChanging] = useState<string | null>(null);
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const hasProjectLines = items.some((li) => li.source_project_id);
  const cols = 5 + (hasProjectLines ? 1 : 0) + (canChangeProject && hasProjectLines ? 1 : 0);

  function close() {
    setChanging(null);
    setPicked(null);
  }

  async function move(line: InvoiceLineItemRow) {
    if (!picked || !line.source_project_id) return;
    const from = projects[line.source_project_id];
    setSaving(true);
    try {
      await changeInvoiceLineProject(line.line_id, picked.id);
      toast.success(
        `Line now bills ${picked.name}. ${from ? projectRowName(from) : line.source_project_id} is back in Ready to Invoice.`,
      );
      close();
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't change the project"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-tcs-border text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
          <th className="px-4 py-2 font-medium">Description</th>
          <th className="px-2 py-2 font-medium">Type</th>
          {hasProjectLines && <th className="px-2 py-2 font-medium">Project</th>}
          <th className="px-2 py-2 text-right font-medium">Hours</th>
          <th className="px-2 py-2 text-right font-medium">Rate</th>
          <th className="px-4 py-2 text-right font-medium">Amount</th>
          {canChangeProject && hasProjectLines && <th className="px-2 py-2" aria-label="Actions" />}
        </tr>
      </thead>
      <tbody>
        {items.map((li) => {
          const proj = li.source_project_id ? projects[li.source_project_id] : undefined;
          const open = changing === li.line_id;
          return (
            <Fragment key={li.line_id}>
              <tr className="border-b border-tcs-border/40 last:border-0">
                <td className="px-4 py-2 text-tcs-text">{li.description}</td>
                <td className="px-2 py-2 text-tcs-text-secondary">{li.line_type.replace('_', ' ')}</td>
                {hasProjectLines && (
                  <td className="px-2 py-2 text-tcs-text-secondary">
                    {li.source_project_id ? (
                      <Link
                        to={`/project-work/${li.source_project_id}`}
                        className="text-tcs-text underline decoration-tcs-border underline-offset-2 hover:decoration-tcs-text-muted"
                      >
                        {proj ? projectRowName(proj) : li.source_project_id}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                )}
                <td className="px-2 py-2 text-right font-mono text-xs text-tcs-text-secondary">
                  {li.hours_quantity != null ? fmtHours(li.hours_quantity) : '—'}
                </td>
                <td className="px-2 py-2 text-right font-mono text-xs text-tcs-text-secondary">
                  {li.unit_rate != null ? fmtMoney(li.unit_rate) : '—'}
                </td>
                <td className="px-4 py-2 text-right font-mono text-xs text-tcs-text">{fmtMoney(li.line_amount)}</td>
                {canChangeProject && hasProjectLines && (
                  <td className="px-2 py-2 text-right">
                    {li.source_project_id && !open && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setChanging(li.line_id);
                          setPicked(null);
                        }}
                      >
                        Change project
                      </Button>
                    )}
                  </td>
                )}
              </tr>
              {open && (
                <tr className="border-b border-tcs-border/40 bg-tcs-bg-muted/40">
                  <td colSpan={cols} className="px-4 py-3">
                    <div className="max-w-md space-y-2">
                      <SmartSelect
                        entity="project"
                        scope={facilityId ? { facility_id: facilityId } : undefined}
                        value={picked?.id ?? li.source_project_id}
                        onChange={(id, row) => setPicked({ id, name: row?.name ?? id })}
                        isSelectable={(row) => isRepointTarget(row, facilityId, li.source_project_id)}
                        placeholder="Search projects at this building…"
                      />
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          size="sm"
                          disabled={!picked || picked.id === li.source_project_id}
                          loading={saving}
                          onClick={() => void move(li)}
                        >
                          {picked && picked.id !== li.source_project_id
                            ? `Move line to ${picked.name}`
                            : 'Pick the project this line should bill'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={close} disabled={saving}>
                          Cancel
                        </Button>
                      </div>
                      <p className="text-xs text-tcs-text-muted">
                        Every line {proj ? projectRowName(proj) : 'this project'} has on this invoice moves, rewritten
                        from the new project. The new project is marked as on this invoice; the old one goes back to
                        Ready to Invoice. No project is deleted, and the change is in the audit trail.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
